import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import useRetryFetch from "./useRetryFetch";
import { getUnderneath, UNDERNEATH_MAX_IDS, type MmUnderneathItem } from "../api/mentalModels";

export type UnderneathMap = Record<string, MmUnderneathItem[]>;

/** Stable, de-duplicated, sorted id list — the query key for a list's batch. */
export const underneathIds = (ids: Array<string | null | undefined>): string[] =>
  Array.from(new Set(ids.filter((id): id is string => Boolean(id)))).sort();

/**
 * One batched "what models are underneath these?" request per visible list
 * (spec 2026-11-03 §D). Chunks past the server cap. Errors resolve to "none".
 */
export function useUnderneath(ids: Array<string | null | undefined>): UnderneathMap | undefined {
  const retryFetch = useRetryFetch();
  const sorted = useMemo(() => underneathIds(ids), [ids]);
  const key = sorted.join(",");
  const { data } = useQuery({
    queryKey: ["mm-underneath", key],
    enabled: sorted.length > 0,
    staleTime: 60_000,
    retry: false,
    queryFn: async () => {
      const chunks: string[][] = [];
      for (let i = 0; i < sorted.length; i += UNDERNEATH_MAX_IDS) chunks.push(sorted.slice(i, i + UNDERNEATH_MAX_IDS));
      const results = await Promise.all(chunks.map((c) => getUnderneath(retryFetch, c)));
      return results.reduce<UnderneathMap>((acc, r) => Object.assign(acc, r.byId ?? {}), {});
    },
  });
  return data;
}
